import {
  CreateTimeslotBodyInterface,
  OfferBodyInterface,
} from '@sport-activities/sdk';
import { add, isBefore } from 'date-fns';
import moment from 'moment-timezone';

import { timeToDate } from '~/core/helpers/date/time-to-date/time-to-date.helper';
import { getAllDatesFromCreationTimeslot } from '~/organizer/activity-planning/helpers/timeslots/get-all-dates-from-creation-timeslot/get-all-dates-from-creation-timeslot.helper';
import { OverloadableTimeslotAttributes } from '~/organizer/activity-planning/services/activity-selection.service';
import { CalendarCreationTimeslotInterface } from '~/organizer/shared/components/shared/calendar/interfaces/recurrent-event.interface';
import { buildLocatableBody } from '~/organizer/shared/helpers/locatable/build-locatable-body/build-locatable-body.helper';

export function convertCreationTimeslotsToTimeslotBodyInterface(
  creationTimeslots: CalendarCreationTimeslotInterface[],
  locale: string,
  defaultPaidOffer: string,
  defaultCurrency: string,
  overloadedAttributes?: OverloadableTimeslotAttributes
): CreateTimeslotBodyInterface[] {
  const timeslotBodies: CreateTimeslotBodyInterface[] = [];
  const maximumAttendeeCapacity = overloadedAttributes?.maximumAttendeeCapacity;
  let offers = overloadedAttributes?.offers;

  creationTimeslots.forEach((creationTimeslot) => {
    const startingPrice = +creationTimeslot.activity.startingPrice?.amount || 0;

    timeslotBodies.push(
      ...getAllDatesFromCreationTimeslot(creationTimeslot).map(
        (date): CreateTimeslotBodyInterface => {
          // If no offer given, fallback on indicative offers
          if (!offers?.length) {
            offers = creationTimeslot.activity.indicativeOffers;
          }

          let offersBody: OfferBodyInterface[] = offers?.map((offer) => {
            const { identifier, price, currency, meta } = offer;
            return {
              identifier,
              price,
              currency,
              name: [buildLocatableBody<string>(locale, offer.name)],
              description: [
                buildLocatableBody<string>(locale, offer.description),
              ],
              meta,
            };
          });

          // If no indicative offers, fallback on default offer
          if (!offersBody?.length) {
            offersBody = [
              {
                identifier: startingPrice > 0 ? defaultPaidOffer : 'free',
                price: startingPrice,
                currency:
                  creationTimeslot.activity.startingPrice?.currency ||
                  defaultCurrency,
              },
            ];
          }

          return setTimeslotProperties(
            locale,
            date,
            maximumAttendeeCapacity,
            offersBody,
            creationTimeslot
          );
        }
      )
    );
  });

  return timeslotBodies;
}

function setTimeslotProperties(
  locale,
  date,
  maximumAttendeeCapacity,
  offersBody,
  creationTimeslot
): CreateTimeslotBodyInterface {
  return {
    activityIdentifier: creationTimeslot.activity.sku.toString(),
    startDate: setStartDate(date, creationTimeslot),
    endDate: setEndDate(
      new Date(setStartDate(date, creationTimeslot)),
      creationTimeslot
    ),
    maximumAttendeeCapacity:
      maximumAttendeeCapacity ??
      creationTimeslot.activity.indicativeMaximumAttendeeCapacity,
    minimumAttendeeCapacity:
      creationTimeslot.activity.indicativeMinimumAttendeeCapacity,
    offers: offersBody,
    liveUrl: creationTimeslot.activity.liveUrl || undefined,
    noteForAttendees: creationTimeslot.activity.noteForAttendees
      ? [
          buildLocatableBody<string>(
            locale,
            creationTimeslot.activity.noteForAttendees
          ),
        ]
      : undefined,
  };
}

function setStartDate(date, timeslot): string {
  const startDateFormatted =
    moment(date).format('yyyy-MM-DD') + ' ' + timeslot.startTime;

  return moment
    ?.tz(startDateFormatted, timeslot.timezone ?? moment?.tz.guess())
    .format()
    .toString();
}

function setEndDate(date, timeslot): string {
  const endDateCalculated = add(date, timeslot.activity.isoDuration);

  return moment
    ?.tz(endDateCalculated, timeslot.timezone ?? moment?.tz.guess())
    .format()
    .toString();
}

export function getDays(startTime: string, endTime: string, days = 0): number {
  return isBefore(timeToDate(startTime), timeToDate(endTime)) ? days : days + 1;
}
